import React, { useRef, useState } from 'react';

import cn from 'classnames';
import Timer from 'react-compound-timer/build';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getKycStatus, resendPhoneNumberOTP, verifyMobileNumber } from 'actions/user';
import OTPInput from 'components/OTPVerification';
import Render from 'components/render';
import { OTP_LENGTH, RECAPTCHA_SITE_KEY } from 'constants/constants';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { errorHasACode } from 'helpers/error';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import {
  getRequestOtpServerErrorMessage,
  getVerifyOtpServerErrorMessage,
} from 'helpers/phoneVerification';
import { addLogsToSentry } from 'helpers/sentry';
import { hidePhoneNumber, noop, parseErrorMsg } from 'helpers/utils';
import {
  userPhoneVerificationTokenSelector,
  userSelector,
} from 'selectors/otherSelectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { CrossIcon } from 'styles/assets/icons';
import { VerifyOtpErrorResponse } from 'types/phoneVerification';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';
import IconButton from 'UIKit/IconButton';

import usePhoneOtp from '../hooks/usePhoneOtp/usePhoneOtp';
import { PhoneOtpActionTypes } from '../hooks/usePhoneOtp/usePhoneOtp.types';
import { MAX_OTP_RESEND_COUNT } from '../PhoneVerification.constants';
import { SetScreenType } from '../PhoneVerification.types';
import styles from './phoneOtp.module.scss';

interface PhoneOtpArgs {
  closeModal: () => void;
  country?: string;
  phone: string;
  setScreen: SetScreenType;
  showNumber?: boolean;
}

const PhoneOtp = ({
  closeModal,
  phone,
  country,
  setScreen,
  showNumber = false,
}: PhoneOtpArgs) => {
  const { t } = useTranslation(['account', 'errors', 'common']);
  const hiddenPhoneNumber = showNumber ? phone : hidePhoneNumber(phone);

  const timerRef = useRef<{ start: Function; reset: Function } | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const phoneVerificationToken = useSelector(userPhoneVerificationTokenSelector);

  const {
    otpValue,
    otpVerificationError,
    phoneOtpDispatch,
    verificationLoading,
    resendSuccess,
  } = usePhoneOtp();

  const dispatch = useAppDispatch();

  const enabledOtp = OTP_LENGTH === otpValue.length;

  const [resendCount, setResendCount] = useState(0);

  const maxResendTriggered = resendCount === MAX_OTP_RESEND_COUNT;

  const user = useAppSelector(userSelector);

  const setTimerRef = (start: Function, reset: Function) => {
    timerRef.current = { start, reset };
  };

  const handleResend = async () => {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.IMPORT_KYC.RESEND_MOBILE_NUMBER_OTP_CLICKED, {
      Flow: 'Fresh kyc',
      site_demarkation: 'Webapp',
    });
    try {
      if (!timerRef.current) {
        return;
      }

      phoneOtpDispatch({
        type: PhoneOtpActionTypes.RESEND_SUCCESS,
        payload: {
          value: false,
        },
      });

      timerRef.current.reset();
      timerRef.current.start();

      const token = await recaptchaRef.current?.executeAsync();
      if (token === undefined) {
        phoneOtpDispatch({
          type: PhoneOtpActionTypes.VERIFICATION_ERROR,
          payload: {
            value: t('errors:utils.captchaError'),
          },
        });

        addLogsToSentry(
          { id: user.id, email: user.email },
          'Recaptcha token is undefined during fresh KYC resend mobile OTP verification'
        );

        return;
      }
      await dispatch(
        resendPhoneNumberOTP({
          phone_verification_jwt_token: phoneVerificationToken as string,
        })
      );

      phoneOtpDispatch({
        type: PhoneOtpActionTypes.RESEND_SUCCESS,
        payload: {
          value: true,
        },
      });

      setResendCount(count => count + 1);
    } catch (err) {
      const { error } = parseErrorMsg(err);

      addLogsToSentry(
        { id: user.id, email: user.email, error },
        'Failure during fresh kyc resend mobile otp verification'
      );

      phoneOtpDispatch({
        type: PhoneOtpActionTypes.VERIFICATION_ERROR,
        payload: {
          value: getRequestOtpServerErrorMessage(error, t),
        },
      });
    }
  };

  const verifyOtpHandler = async () => {
    try {
      phoneOtpDispatch({
        payload: { value: true },
        type: PhoneOtpActionTypes.VERIFICATION_LOADING,
      });

      await dispatch(
        verifyMobileNumber({
          phone_verification_jwt_token: phoneVerificationToken as string,
          otp: otpValue,
        })
      );

      await dispatch(getKycStatus());
      phoneOtpDispatch({
        payload: { value: false },
        type: PhoneOtpActionTypes.VERIFICATION_LOADING,
      });

      trackMixpanelEvent('Mobile number verified - Identity Verification', {
        user_country: country,
      });

      setScreen('successScreen');
    } catch (err) {
      console.error(err);
      const { error } = parseErrorMsg(err);
      trackMixpanelEvent(MIXPANEL_EVENT_NAMES.IMPORT_KYC.MOBILE_OTP_VERIFICATION_FAILED, {
        Flow: 'Fresh kyc',
        error: error.code || 'unknown',
        site_demarkation: 'Webapp',
      });

      if (errorHasACode<VerifyOtpErrorResponse>(error)) {
        phoneOtpDispatch({
          type: PhoneOtpActionTypes.VERIFICATION_ERROR,
          payload: {
            value: getVerifyOtpServerErrorMessage(error, t),
          },
        });
      } else {
        setScreen('errorScreen');
      }
    }
  };

  const handleOtpChange = (otp: string) => {
    phoneOtpDispatch({
      type: PhoneOtpActionTypes.UPDATE_OTP_VALUE,
      payload: {
        value: otp,
      },
    });
  };

  return (
    <Box className={styles.phonOtpContainer}>
      <Box flex="inline" textColor="primary" className={styles.headerContainer}>
        <Box as="h4" autoMargin="both">
          {t('account:profile.phoneVerification')}
        </Box>
        <IconButton
          data-testid="closeEnterPhone"
          icon={<CrossIcon />}
          onClick={closeModal}
          onKeyDown={noop}
        />
      </Box>
      <Box
        className={styles.phoneOtpBody}
        textColor="secondary"
        flex="flex"
        flexDir="column"
      >
        <Box>
          {t('account:phoneAuthCode', {
            digit: 6,
            phone: hiddenPhoneNumber,
          })}
        </Box>
        <OTPInput
          autoFocus
          isNumberInput
          length={OTP_LENGTH}
          className={styles.otpContainer}
          hideSendCodeRightSection
          inputClassName={cn(styles.otpInput, {
            [styles.invalid]: Boolean(otpVerificationError),
          })}
          onChangeOTP={handleOtpChange}
          testID="phoneVerification-otpInput"
        />
        <Box
          className={styles.timerBox}
          flex="inline"
          justifyContent="between"
          alignItems="center"
        >
          <Box as="span" renderWhen={resendSuccess} fontSize="sm2" textColor="positive">
            {t('account:profile.otpSuccess')}
          </Box>
          <Render when={!maxResendTriggered}>
            <Timer
              initialTime={30000}
              direction="backward"
              checkpoints={[
                {
                  time: 0,
                  callback: noop,
                },
              ]}
            >
              {({ start, reset, getTime }) => {
                const timeInSecs = Math.round(getTime() / 1000);
                // Binding the start and reset function with the ref
                setTimerRef(start, reset);

                return (
                  <Box flex="inline" autoMargin="left">
                    {timeInSecs > 0 && (
                      <Box as="span" className={styles.timer}>
                        {timeInSecs}s
                      </Box>
                    )}
                    <DeltaButton
                      useBasicButton
                      type="button"
                      className={styles.resendCodeBtn}
                      disabled={timeInSecs > 0}
                      onClick={handleResend}
                      data-testid="phoneOtp-resendCodeButton"
                    >
                      {t('common:resendCode')}
                    </DeltaButton>
                  </Box>
                );
              }}
            </Timer>
          </Render>
        </Box>
        <Box
          renderWhen={Boolean(otpVerificationError)}
          textColor="negative"
          fontSize="sm2"
        >
          {otpVerificationError}
        </Box>
        <DeltaButton
          onClick={verifyOtpHandler}
          disabled={!enabledOtp}
          loading={verificationLoading}
          data-testid="phoneVerifyOtp"
          fullWidth
        >
          {t('account:profile.submitCode')}
        </DeltaButton>
      </Box>
      <ReCAPTCHA
        sitekey={RECAPTCHA_SITE_KEY!}
        ref={recaptchaRef}
        theme="dark"
        size="invisible"
      />{' '}
    </Box>
  );
};

export default PhoneOtp;
